export function ensureError(value: unknown): Error {
  if (value instanceof Error) return value
  return new Error(
    `This value was thrown as is, not through an Error: ${stringifyThrown(value)}`,
    { cause: value },
  )
}

const MAX_THROWN_CHARS = 4000
const MAX_FIELD_CHARS = 500

function clip(text: string, max: number): string {
  if (text.length <= max) return text
  return `${text.slice(0, max)}… [truncated, ${text.length} chars]`
}

/**
 * Neon WS / pg errors are often plain objects whose useful fields (`message`,
 * `code`, `query`) are non-enumerable, so JSON.stringify renders them as `{}`.
 * `getOwnPropertyNames` sees them. Those same fields can be megabytes — a pg
 * error carries the failed statement — and the result becomes `Error.message`,
 * which {@link formatErrorForSlack} does not cap.
 *
 * No `node:util` here: this module is on the client graph via `SearchProvider`,
 * where a Node builtin only buys a polyfill and a different `inspect`.
 */
function stringifyThrown(value: unknown): string {
  if (typeof value === 'string') return clip(value, MAX_THROWN_CHARS)
  if (typeof value !== 'object' || value === null) return String(value)
  try {
    const fields = Object.getOwnPropertyNames(value).map((key) => {
      let rendered: string
      try {
        rendered =
          JSON.stringify((value as Record<string, unknown>)[key]) ?? 'undefined'
      } catch {
        rendered = '[unserialisable]'
      }
      return `${key}: ${clip(rendered, MAX_FIELD_CHARS)}`
    })
    return clip(`{ ${fields.join(', ')} }`, MAX_THROWN_CHARS)
  } catch {
    // Never let the error path throw and lose the original failure.
    return String(value)
  }
}

/** Formats an error as `message` + fenced stack for Slack; safe when stack is missing. */
export function formatErrorForSlack(
  error: Error,
  maxStackChars = 1500,
): string {
  if (!error.stack) return error.message
  const safeStack = error.stack
    .slice(0, maxStackChars)
    .replaceAll('```', '` ` `')
  return `${error.message}\n\`\`\`${safeStack}\`\`\``
}
